import { useState } from 'react'
import { ActionIcon, Badge, Button, Input, Loader, Select, Table } from '@mantine/core'
import { IconSearch, IconStethoscope, IconX } from '@tabler/icons-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'
import dayjs from 'dayjs'
import { Th } from '@/components'
import { ACCENT } from '@/components/ui/primaryButton.tokens'
import { useDebounce } from '@/hooks'
import {
  usePractitioners,
  type PractitionerRoleFilter,
  type PractitionerRow,
} from '@/hooks/usePractitioners'
import { usePractitionerOpsScope } from '@/hooks/usePractitionerOpsScope'
import { formatProfessionalRole, LICENSE_STATUSES } from '@behuman/core-contract'
import { SNOMED_USER_ROLES } from '@/utils'
import { describeDoseSpotClinicianKind } from '@/utils/doseSpotClinicianKind'
import { formatDateTime } from '@/utils.ts'

// The Organization choice lives in the URL so a reload and a shared link land on the same list.
export const ORGANIZATION_QUERY_PARAM = 'organizationId'

const COLUMNS = [
  'Name',
  'Role',
  'NPI',
  'Licensed states',
  'Professional role',
  'Earliest license expiry',
  'DoseSpot',
]

type RoleFilter = 'all' | PractitionerRoleFilter

const ROLE_FILTER_OPTIONS: { value: RoleFilter; label: string }[] = [
  { value: 'all', label: 'All roles' },
  { value: 'practitioner', label: 'Practitioner' },
  { value: 'care_coordinator', label: 'Care Coordinator' },
]

const SCROLL_TARGET = 'provider-table-scroll'
const EMPTY_CELL = <span className="text-[#B4B9C0]">—</span>

function earliestExpiry(row: PractitionerRow): string | undefined {
  return row.licenses
    .map((license) => license.expiryDate)
    .filter((date): date is string => Boolean(date))
    .sort()[0]
}

const LICENSE_STATUS_LABEL = new Map<string, string>(
  LICENSE_STATUSES.map((s) => [s.value, s.label]),
)

// An Active license is just its state; any other License Status follows in parentheses.
function licensedStates(row: PractitionerRow): string {
  return row.licenses
    .map((license) =>
      license.status && license.status !== 'active'
        ? `${license.state} (${LICENSE_STATUS_LABEL.get(license.status) ?? license.status})`
        : license.state,
    )
    .join(', ')
}

/**
 * Provider Operations: the Org Members of one Organization with their Credentials at a
 * glance, mirroring the Patients list (name search, role filter, name sort, infinite scroll
 * in pages of fifty). A Care Coordinator's Organization is implied and named in the header;
 * a caller whose scope spans several Organizations (a super admin, or a coordinator in two)
 * picks one from a dropdown, defaulting to the first by name. The middleware refuses an
 * Organization outside the caller's scope, so this page only ever narrows what is shown.
 */
export function ProviderList() {
  const navigate = useNavigate()
  const { organizations } = usePractitionerOpsScope()
  const [searchParams, setSearchParams] = useSearchParams()

  const requestedId = searchParams.get(ORGANIZATION_QUERY_PARAM)
  // The scope arrives ordered by name, so [0] is the default the spec asks for. An id the
  // caller may not see (stale link, another Organization) falls back to that default.
  const organization = organizations.find((org) => org.id === requestedId) ?? organizations[0]

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearch = useDebounce(searchQuery, 500)
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('practitioner')
  const [nameSort, setNameSort] = useState<'asc' | 'desc' | null>(null)

  // Every input is part of the query key, so changing search, filter or Organization starts
  // over from the first page.
  const {
    practitioners,
    total,
    isLoading,
    isPlaceholderData,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
  } = usePractitioners(organization?.id ?? '', {
    search: debouncedSearch,
    role: roleFilter === 'all' ? undefined : roleFilter,
    nameSort: nameSort ?? undefined,
  })

  const selectOrganization = (id: string | null) => {
    if (!id) return
    setSearchParams(
      (previous) => {
        previous.set(ORGANIZATION_QUERY_PARAM, id)
        return previous
      },
      { replace: true },
    )
  }

  // Same cycle as the Patients list: descending first, then ascending, then off.
  const toggleNameSort = () =>
    setNameSort((prev) => (prev === null ? 'desc' : prev === 'desc' ? 'asc' : null))

  // The detail page reads the Organization from the query, so name the one on screen
  // explicitly: the URL is empty while the default is shown, and may still carry an id the
  // list fell back from.
  const openPractitioner = (row: PractitionerRow) => {
    // While a newly chosen Organization loads, the rows on screen are still the previous
    // Organization's (`keepPreviousData`); a link built from them would name the wrong one.
    if (isPlaceholderData) return
    const search = new URLSearchParams(searchParams)
    if (organization) search.set(ORGANIZATION_QUERY_PARAM, organization.id)
    void navigate({ pathname: `/providers/${row.practitionerId}`, search: search.toString() })
  }

  const renderExpiry = (row: PractitionerRow) => {
    const expiry = earliestExpiry(row)
    if (!expiry) return EMPTY_CELL
    const formatted = formatDateTime(expiry, '/', false, true)
    return dayjs(expiry).isBefore(dayjs(), 'day') ? (
      <span className="font-semibold text-[#D9480F]" title="License expired">
        {formatted}
      </span>
    ) : (
      formatted
    )
  }

  const renderRow = (row: PractitionerRow) => {
    const isCareCoordinator = row.roles.some(
      (role) => role.code === SNOMED_USER_ROLES.HOSPITAL_MANAGER.code,
    )
    const states = licensedStates(row)
    // A Care Coordinator holds no degree the console cares about, whatever is stored.
    const professionalRoleCell = isCareCoordinator
      ? ''
      : formatProfessionalRole(row.professionalRole, row.specialty)
    return (
      <Table.Tr
        key={row.practitionerId}
        className={`h-[52px] border-b border-[#F0F1F3] text-[13px] text-[#4A5160] transition-colors ${isPlaceholderData ? 'opacity-50' : 'cursor-pointer hover:bg-[#FAFBFC]'}`}
        aria-busy={isPlaceholderData}
        onClick={() => openPractitioner(row)}
      >
        <Table.Td>
          {row.displayName ? (
            <span className="font-semibold text-[#1F2733]">{row.displayName}</span>
          ) : (
            EMPTY_CELL
          )}
        </Table.Td>
        <Table.Td>
          {isCareCoordinator ? (
            <Badge size="sm" variant="outline">
              Care Coordinator
            </Badge>
          ) : (
            <Badge size="sm" color="gray" variant="light">
              Practitioner
            </Badge>
          )}
        </Table.Td>
        <Table.Td>{row.npiNumber || EMPTY_CELL}</Table.Td>
        <Table.Td>{states || EMPTY_CELL}</Table.Td>
        <Table.Td>{professionalRoleCell || EMPTY_CELL}</Table.Td>
        <Table.Td>{renderExpiry(row)}</Table.Td>
        <Table.Td>
          {row.doseSpotClinicianKind
            ? describeDoseSpotClinicianKind(row.doseSpotClinicianKind)
            : EMPTY_CELL}
        </Table.Td>
      </Table.Tr>
    )
  }

  return (
    <div className="box-border h-[calc(100vh-64px)] bg-[#F6F7F9] p-4 text-[#1F2733]">
      <div className="flex h-full flex-col gap-4 rounded-[18px] border border-[#E7E9EC] bg-white px-6 pt-6 pb-4 shadow-[0_1px_3px_rgba(16,24,40,.05),0_12px_30px_rgba(16,24,40,.05)]">
        <div className="flex w-full flex-wrap items-center justify-between gap-5">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2.5">
              <IconStethoscope size={22} className="text-[#E5533D]" stroke={2} />
              <h1 className="text-xl font-bold tracking-tight text-[#1F2733]">
                Provider Operations
              </h1>
              {organization && !isLoading && (
                <span className="rounded-full bg-[#F1F2F4] px-2.5 py-0.5 text-sm font-semibold text-[#6B7280]">
                  {total}
                </span>
              )}
            </div>
            {organization && organizations.length === 1 && (
              <p className="text-sm font-medium text-[#6B7280]">{organization.name}</p>
            )}
            {organization && !isLoading && (
              <p className="text-sm font-medium text-[#6B7280]">
                Showing {practitioners.length} of {total} member{total === 1 ? '' : 's'}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Search by name or full email..."
              aria-label="Search members"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              radius="md"
              leftSection={<IconSearch size={16} className="text-[#9AA0A8]" />}
              rightSection={
                searchQuery ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Clear search"
                    onClick={() => setSearchQuery('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
              rightSectionPointerEvents="all"
              className="w-full min-w-[200px] sm:w-[280px]"
              classNames={{
                input:
                  '!h-[32px] !min-h-[32px] !rounded-md !border-[#D8DBE0] !text-[16px] text-[#33383F]',
              }}
            />
            <Select
              w={190}
              aria-label="Filter by role"
              data={ROLE_FILTER_OPTIONS}
              value={roleFilter}
              onChange={(value) => setRoleFilter((value as RoleFilter | null) ?? 'all')}
              allowDeselect={false}
            />
            {organizations.length > 1 && (
              <Select
                aria-label="Organization"
                data={organizations.map((org) => ({ value: org.id, label: org.name }))}
                value={organization?.id ?? null}
                onChange={selectOrganization}
                allowDeselect={false}
                searchable
                w={280}
              />
            )}
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <div id={SCROLL_TARGET} className="h-full overflow-y-auto">
            <InfiniteScroll
              dataLength={practitioners.length}
              next={fetchNextPage}
              hasMore={!!hasNextPage}
              loader={<Loader color="blue" />}
              scrollableTarget={SCROLL_TARGET}
              style={{ display: 'contents' }}
            >
              <Table className="w-full table-fixed">
                <Table.Thead className="bg-white" style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                  <Table.Tr className="h-[40px] border-b border-[#EAECEF] !bg-white">
                    {COLUMNS.map((title) =>
                      title === 'Name' ? (
                        <Th
                          key={title}
                          className="text-[11px] font-semibold tracking-wider text-[#8A94A6] uppercase"
                          sorted={nameSort !== null}
                          reversed={nameSort === 'asc'}
                          onSort={toggleNameSort}
                        >
                          {title}
                        </Th>
                      ) : (
                        <Th
                          key={title}
                          className="text-[11px] font-semibold tracking-wider text-[#8A94A6] uppercase"
                        >
                          {title}
                        </Th>
                      ),
                    )}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {isError ? (
                    <Table.Tr>
                      <Table.Td colSpan={COLUMNS.length} className="py-10 text-center">
                        <p className="text-[#6B7280]">Failed to load members.</p>
                        <Button variant="subtle" color={ACCENT} onClick={() => void refetch()}>
                          Try again
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ) : practitioners.length === 0 && !isLoading ? (
                    <Table.Tr>
                      <Table.Td
                        colSpan={COLUMNS.length}
                        className="py-10 text-center text-[#6B7280]"
                      >
                        No members to show.
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    practitioners.map(renderRow)
                  )}
                </Table.Tbody>
              </Table>
            </InfiniteScroll>
          </div>
        </div>
      </div>
      {isLoading && organization && (
        <div className="absolute top-0 left-0 z-100 flex h-[100vh] w-[100vw] items-center justify-center bg-[rgba(255,255,255,0.5)]">
          <Loader />
        </div>
      )}
    </div>
  )
}
